// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { CustomThemeProvider, Tabs, usePortal, useQueryFetch } from 'sonata-react-components';

import { getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { downloadFileFromBlob } from '@/ts-helpers/fileDownload.helper';
import { downloadZip } from '@/ts-modules/reporting/reporting.resources';
import { MyReportsModalStyle } from './MyReportsModal.style';
import { ReportsDownloadsTab } from './ReportsDownloadsTab';
import { ScheduledReportsTab } from './ScheduledReportsTab';

import type { ReportDownloadInterface } from 'sonata-static-storage/lib/schemas/report/reportDownload.schema';
import type { ScheduleReportInterfaceWithAdditionalData } from 'sonata-static-storage/lib/schemas/scheduleReport/scheduleReport.schema';
import type { ExportFileFormat, ReportConfigInitialData } from '../types';

type TabId = 'reportsDownloads' | 'scheduledReports';

type Props = {
  defaultTabOpen?: TabId;
  id: string;
  isOpen: boolean;
  onClose: (reportToLoad?: ReportConfigInitialData) => void;
};

type State = {
  selectedIds: string[];
};

function MyReportsModal({ defaultTabOpen = 'reportsDownloads', id, isOpen, onClose }: Props) {
  const { formatMessage } = useIntl();
  const user = useSelector(getCurrentUser);
  const campaigns = useSelector(getSelectedCampaigns);
  const campaignsIds = campaigns.map((campaign: any) => campaign.id);
  const modalRef: any = useRef();
  const targetBody = usePortal('myReportsModalPortal');
  const [{ selectedIds }, setState] = useState<State>({
    selectedIds: []
  });

  const { isFetching, refetch } = useQueryFetch(
    ['multiDownload', selectedIds],
    async () => downloadZip(user.id, selectedIds),
    {
      enabled: false,
      onSuccess: blob => {
        downloadFileFromBlob(blob);
      }
    }
  );
  useEffect(() => {
    isOpen ? modalRef?.current?.openModal() : modalRef?.current?.closeModal();
  }, [isOpen]);

  useEffect(() => {
    if (selectedIds.length) {
      refetch();
    }
  }, [refetch, selectedIds]);

  const handleDownloadMultipleReports = (ids: string[]) => {
    setState(prevState => ({
      ...prevState,
      selectedIds: ids
    }));
  };

  const handleTabChange = () => {
    setState(prevState => ({
      ...prevState,
      selectedIds: []
    }));
  };

  const handleReportDownloadLoad = (report: ReportDownloadInterface) => {
    onClose({
      entityId: report._id,
      name: report.name,
      storedReportType: 'download',
      tabsOptions: report.tabsOptions,
      type: report.type
    });
  };

  const handleScheduleReportLoad = (schedule: ScheduleReportInterfaceWithAdditionalData) => {
    onClose({
      entityId: schedule._id,
      name: schedule.name || '',
      scheduleConfig: schedule,
      storedReportType: 'schedule',
      tabsOptions: schedule.report.tabsOptions,
      type: schedule.report.type as ExportFileFormat
    });
  };

  const tabs: React.ComponentProps<typeof Tabs>['tabs'] = [
    {
      component: (
        <ReportsDownloadsTab
          campaignsIds={campaignsIds}
          id={`reportsDownloads-${id}`}
          isDownloadLoading={isFetching}
          onDownloadMultipleReports={handleDownloadMultipleReports}
          onReportLoad={handleReportDownloadLoad}
          userId={user.id}
        />
      ),
      id: 'reportsDownloads',
      title: formatMessage({
        defaultMessage: 'Reports Downloads',
        id: 'reporting.reportingDownload.myReportsModal.reportsDownloadsTab.title'
      })
    },
    {
      component: (
        <ScheduledReportsTab
          campaignsIds={campaignsIds}
          id={`scheduledReports-${id}`}
          isDownloadLoading={isFetching}
          onDownloadMultipleReports={handleDownloadMultipleReports}
          onReportLoad={handleScheduleReportLoad}
          userId={user.id}
        />
      ),
      id: 'scheduledReports',
      title: formatMessage({
        defaultMessage: 'Scheduled Reports',
        id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.title'
      })
    }
  ];

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <MyReportsModalStyle
        className="myReportsModal"
        closeModalCallback={onClose}
        id={id}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'My Reports',
          id: 'reporting.reportingDownload.myReportsModal.title'
        })}
      >
        <Tabs className="myReportsModal__tabs" initialTab={defaultTabOpen} onChangeTab={handleTabChange} tabs={tabs} />
      </MyReportsModalStyle>
    </CustomThemeProvider>,
    targetBody
  );
}

export default MyReportsModal;
